import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { tuyau } from "@/config/tuyau";
import type {
  ChefOnboardingFormData,
  LoginFormData,
  ResetPasswordFormData,
  SignUpFormData,
} from "@/domain/auth/auth.type";

export const authKeys = {
  all: ["auth"] as const,
  session: () => [...authKeys.all, "session"] as const,
  verifyEmail: (token: string) =>
    [...authKeys.all, "verify-email", token] as const,
};

export const authQueries = {
  session: () =>
    queryOptions({
      queryKey: authKeys.session(),
      queryFn: async () => await tuyau.auth.getUser({}),
      retry: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      meta: {
        silentErrorToast: true,
      },
    }),

  verifyEmail: (token: string) =>
    queryOptions({
      queryKey: authKeys.verifyEmail(token),
      queryFn: async () => await tuyau.auth.verifyEmail({ query: { token } }),
      enabled: Boolean(token),
      retry: false,
    }),
};

export const authMutations = {
  logout: () =>
    mutationOptions({
      mutationFn: async () => await tuyau.auth.logout({}),
    }),

  login: () =>
    mutationOptions({
      mutationFn: async (data: LoginFormData) =>
        await tuyau.auth.login({ body: data }),
    }),

  register: () =>
    mutationOptions({
      mutationFn: async (data: Omit<SignUpFormData, "confirmPassword">) =>
        await tuyau.auth.register({ body: data }),
    }),

  forgotPassword: () =>
    mutationOptions({
      meta: {
        silentErrorToast: true,
      },
      mutationFn: async (data: ResetPasswordFormData) =>
        await tuyau.auth.forgotPassword({ body: data }),
    }),

  resetPassword: () =>
    mutationOptions({
      mutationFn: async ({
        password,
        token,
      }: {
        password: string;
        token: string;
      }) =>
        await tuyau.auth.resetPassword({
          body: { password, token },
        }),
    }),

  resendEmailVerification: () =>
    mutationOptions({
      mutationFn: async () => await tuyau.auth.resendEmailVerification({}),
    }),

  createChefProfile: () =>
    mutationOptions({
      mutationFn: async (data: ChefOnboardingFormData) =>
        await tuyau.chefs.store({ body: data }),
    }),

  impersonateChef: () =>
    mutationOptions({
      mutationFn: async ({ chefId }: { chefId: number }) =>
        await tuyau.auth.impersonate({ params: { chefId } }),
    }),
};

export async function getCurrentUserSafe() {
  const [user] = await tuyau.auth.getUser({}).safe();
  return user;
}
